import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addMonths,
  endOfDay,
  isSameDay,
  localTimeValue,
  monthGrid,
  startOfDay,
  startOfMonth,
  withTimeOf,
} from '../../utils/date';

interface Props {
  value: [Date, Date];
  onChange: (range: [Date, Date]) => void;
  /** Mirrors MAX_RANGE in backend/routes/sensors.py. */
  maxRangeDays?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const SHORTCUTS: Array<{ label: string; days: number; endsToday: boolean }> = [
  { label: 'היום', days: 0, endsToday: true },
  { label: 'אתמול', days: 1, endsToday: false },
  { label: '7 ימים אחרונים', days: 6, endsToday: true },
  { label: '30 ימים אחרונים', days: 29, endsToday: true },
];

function formatTrigger(date: Date) {
  return date.toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function parseTimeInto(day: Date, value: string): Date | null {
  const parts = value.split(':').map(Number);
  if (parts.length < 2 || parts.some(part => !Number.isFinite(part))) return null;
  const next = new Date(day);
  next.setHours(parts[0], parts[1], parts[2] ?? 0, 0);
  return next;
}

export default function SensorRangePicker({ value, onChange, maxRangeDays = 30 }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<[Date, Date]>(value);
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [hovered, setHovered] = useState<Date | null>(null);
  const [month, setMonth] = useState(() => startOfMonth(addMonths(value[1], -1)));
  const containerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Seed the draft from the applied range each time the picker opens. Deliberately
  // not keyed on `value` too: auto-refresh re-anchors the range every 30s, which
  // would otherwise wipe a selection in progress.
  useEffect(() => {
    if (!open) return;
    setDraft(valueRef.current);
    setPendingStart(null);
    setHovered(null);
    setMonth(startOfMonth(addMonths(valueRef.current[1], -1)));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const today = useMemo(() => endOfDay(new Date()), []);

  const error = useMemo(() => {
    if (pendingStart) return 'בחרו תאריך סיום';
    if (draft[1] <= draft[0]) return 'טווח לא תקין';
    if (draft[1].getTime() - draft[0].getTime() > maxRangeDays * DAY_MS) {
      return `טווח מרבי ${maxRangeDays} ימים`;
    }
    return null;
  }, [draft, maxRangeDays, pendingStart]);

  const selectDay = (day: Date) => {
    if (!pendingStart || day < startOfDay(pendingStart)) {
      setPendingStart(day);
      setDraft([withTimeOf(startOfDay(day), draft[0]), draft[1]]);
      return;
    }
    setDraft([withTimeOf(startOfDay(pendingStart), draft[0]), endOfDay(day)]);
    setPendingStart(null);
    setHovered(null);
  };

  const applyShortcut = (days: number, endsToday: boolean) => {
    const anchor = endsToday ? new Date() : new Date(Date.now() - DAY_MS);
    setDraft([startOfDay(new Date(anchor.getTime() - days * DAY_MS)), endOfDay(anchor)]);
    setPendingStart(null);
    setHovered(null);
  };

  const updateTime = (index: 0 | 1, raw: string) => {
    const next = parseTimeInto(draft[index], raw);
    if (!next) return;
    setDraft(current => (index === 0 ? [next, current[1]] : [current[0], next]));
  };

  const confirm = () => {
    if (error) return;
    onChange(draft);
    setOpen(false);
  };

  // While a start is pending, preview the span out to the hovered day.
  const previewFrom = pendingStart ? startOfDay(pendingStart) : draft[0];
  const previewTo = pendingStart
    ? (hovered && hovered > pendingStart ? endOfDay(hovered) : endOfDay(pendingStart))
    : draft[1];

  const renderMonth = (base: Date) => (
    <div className="w-56">
      <div className="text-center text-xs font-bold text-espresso-700 mb-2">
        {base.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' })}
      </div>
      <div className="grid grid-cols-7 gap-px text-[10px] text-espresso-400 mb-1">
        {WEEKDAYS.map(day => <div key={day} className="text-center">{day}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {monthGrid(base.getFullYear(), base.getMonth()).map(day => {
          const outside = day.getMonth() !== base.getMonth();
          const future = startOfDay(day) > today;
          const tooFar = pendingStart != null
            && day > pendingStart
            && day.getTime() - startOfDay(pendingStart).getTime() > maxRangeDays * DAY_MS;
          const disabled = future || tooFar;
          const isStart = isSameDay(day, previewFrom);
          const isEnd = isSameDay(day, previewTo);
          const inRange = day >= startOfDay(previewFrom) && day <= previewTo;

          let tone = 'text-espresso-700 hover:bg-espresso-100';
          if (isStart || isEnd) tone = 'bg-espresso-800 text-cream-100 font-bold';
          else if (inRange) tone = 'bg-espresso-100 text-espresso-800';

          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => selectDay(day)}
              onMouseEnter={() => setHovered(day)}
              className={`h-7 rounded text-[11px] ${tone}
                ${outside ? 'opacity-35' : ''}
                ${disabled ? 'opacity-30 cursor-not-allowed hover:bg-transparent' : ''}
                ${isSameDay(day, new Date()) ? 'ring-1 ring-gold-500' : ''}`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-xs text-espresso-500">
        טווח תאריכים
        <button
          type="button"
          onClick={() => setOpen(current => !current)}
          className="input mt-1 py-1.5 w-auto text-right whitespace-nowrap"
        >
          {formatTrigger(value[0])} — {formatTrigger(value[1])}
        </button>
      </label>

      {open && (
        <div className="card absolute z-50 mt-2 p-4 shadow-medium end-0 w-max" dir="rtl">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setMonth(current => addMonths(current, -1))}
              className="px-2 py-1 rounded hover:bg-espresso-100 text-espresso-600"
              aria-label="חודש קודם"
            >
              ›
            </button>
            <div className="flex gap-4">
              {/* RTL: the later month sits on the left. */}
              {renderMonth(addMonths(month, 1))}
              {renderMonth(month)}
            </div>
            <button
              type="button"
              onClick={() => setMonth(current => addMonths(current, 1))}
              className="px-2 py-1 rounded hover:bg-espresso-100 text-espresso-600"
              aria-label="חודש הבא"
            >
              ‹
            </button>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-cream-300 pt-3">
            {SHORTCUTS.map(shortcut => (
              <button
                key={shortcut.label}
                type="button"
                onClick={() => applyShortcut(shortcut.days, shortcut.endsToday)}
                className="px-2.5 py-1 rounded-lg bg-espresso-100 text-espresso-700 text-xs"
              >
                {shortcut.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3 border-t border-cream-300 mt-3 pt-3">
            <label className="text-xs text-espresso-500">
              שעת התחלה
              <input
                type="time"
                step="1"
                value={localTimeValue(draft[0])}
                onChange={event => updateTime(0, event.target.value)}
                className="input mt-1 py-1 w-auto"
              />
            </label>
            <label className="text-xs text-espresso-500">
              שעת סיום
              <input
                type="time"
                step="1"
                value={localTimeValue(draft[1])}
                onChange={event => updateTime(1, event.target.value)}
                className="input mt-1 py-1 w-auto"
              />
            </label>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-cream-300 mt-3 pt-3">
            <span className={`text-xs ${error ? 'text-red-600' : 'text-espresso-400'}`}>
              {error ?? `${formatTrigger(draft[0])} — ${formatTrigger(draft[1])}`}
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary py-1.5">
                ביטול
              </button>
              <button type="button" onClick={confirm} disabled={!!error} className="btn-primary py-1.5">
                אישור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
